import type { QuestionnaireResponse } from "@medplum/fhirtypes";
import {
  asMemberId,
  asPatientId,
  asPerformanceMeasureId,
  asPractitionerId,
  asVisitId,
  type PerformanceMeasureFacts,
  parsePinnedCanonical,
} from "@repo/domain";
import {
  PERFORMANCE_MEASURE_KIND,
  PERFORMANCE_MEASURE_KIND_SYSTEM,
  PERFORMANCE_MEASURE_OUTCOME_URL,
} from "@repo/fhir-systems";
import {
  PATIENT_ENTITY_ID_SYSTEM,
  PERFORMANCE_MEASURE_ENTITY_ID_SYSTEM,
  PRACTITIONER_ENTITY_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
} from "@repo/fhir-systems/identifiers";
import { answerContent, toStoredAnswer } from "./questionnaire-instance-storage";

export function isPerformanceMeasureRow(row: QuestionnaireResponse): boolean {
  return (
    row.meta?.tag?.some(
      (tag) => tag.system === PERFORMANCE_MEASURE_KIND_SYSTEM && tag.code === PERFORMANCE_MEASURE_KIND,
    ) ?? false
  );
}

export function readPerformanceMeasureRow(row: QuestionnaireResponse): PerformanceMeasureFacts {
  const required = (value: string | undefined, field: string): string => {
    if (!value) throw new Error(`Performance measure ${row.id ?? "unknown"} has no ${field}`);
    return value;
  };
  if (!isPerformanceMeasureRow(row)) throw new Error("Not a performance measure");
  const definition = parsePinnedCanonical(row.questionnaire);
  if (!definition) throw new Error("Performance measure requires its pinned definition");
  const outcome = row.extension?.find((e) => e.url === PERFORMANCE_MEASURE_OUTCOME_URL)?.valueCode;
  if (
    outcome !== undefined &&
    outcome !== "performed" &&
    outcome !== "did-not-perform" &&
    outcome !== "unable-to-perform"
  )
    throw new Error("Unknown performance measure outcome");
  if (
    (row.status !== "in-progress" && row.status !== "completed") ||
    (row.status === "completed" && !outcome) ||
    (row.status === "in-progress" && outcome === "performed")
  )
    throw new Error("Performance measure status and outcome disagree");
  const clinician = required(
    row.author?.identifier?.system === PRACTITIONER_ENTITY_ID_SYSTEM ? row.author.identifier.value : undefined,
    "clinician",
  );
  if (row.source?.identifier?.system !== PRACTITIONER_ENTITY_ID_SYSTEM || row.source.identifier.value !== clinician)
    throw new Error("Performance measure author and source disagree");
  return {
    id: asPerformanceMeasureId(
      required(
        row.identifier?.system === PERFORMANCE_MEASURE_ENTITY_ID_SYSTEM ? row.identifier.value : undefined,
        "identity",
      ),
    ),
    patient: asPatientId(
      required(
        row.subject?.identifier?.system === PATIENT_ENTITY_ID_SYSTEM ? row.subject.identifier.value : undefined,
        "patient",
      ),
    ),
    visit: asVisitId(
      required(
        row.encounter?.identifier?.system === VISIT_ENTITY_ID_SYSTEM ? row.encounter.identifier.value : undefined,
        "visit",
      ),
    ),
    clinician: asPractitionerId(clinician),
    recordedAt: required(row.authored, "recorded time"),
    status: row.status === "completed" ? "closed" : "open",
    definition,
    ...(outcome ? { outcome } : {}),
    grades: (row.item ?? []).map((item) => {
      if (item.answer?.length !== 1 || !item.answer[0]) throw new Error("Performance task requires one answer");
      const answer = answerContent(row, item.answer[0]);
      if (!answer) throw new Error("Performance task requires one answer");
      return { task: asMemberId(item.linkId), answer };
    }),
  };
}

/** Optimistic writes use the same FHIR facts that authoritative sync reads. */
export function writePerformanceMeasureRow(
  facts: PerformanceMeasureFacts,
  prior?: QuestionnaireResponse,
): QuestionnaireResponse {
  const performer = {
    ...(prior?.author?.reference ? { reference: prior.author.reference } : {}),
    identifier: { system: PRACTITIONER_ENTITY_ID_SYSTEM, value: facts.clinician },
  };
  return {
    ...prior,
    resourceType: "QuestionnaireResponse",
    id: prior?.id ?? facts.id,
    identifier: { system: PERFORMANCE_MEASURE_ENTITY_ID_SYSTEM, value: facts.id },
    meta: prior?.meta ?? { tag: [{ system: PERFORMANCE_MEASURE_KIND_SYSTEM, code: PERFORMANCE_MEASURE_KIND }] },
    status: facts.status === "closed" ? "completed" : "in-progress",
    questionnaire: `${facts.definition.url}|${facts.definition.version}`,
    subject: { ...prior?.subject, identifier: { system: PATIENT_ENTITY_ID_SYSTEM, value: facts.patient } },
    encounter: { ...prior?.encounter, identifier: { system: VISIT_ENTITY_ID_SYSTEM, value: facts.visit } },
    author: performer,
    source: performer,
    authored: facts.recordedAt,
    item: facts.grades.map((grade) => ({
      linkId: grade.task,
      answer: [toStoredAnswer(grade.answer)],
    })),
    extension: [
      ...(prior?.extension ?? []).filter((e) => e.url !== PERFORMANCE_MEASURE_OUTCOME_URL),
      ...(facts.outcome ? [{ url: PERFORMANCE_MEASURE_OUTCOME_URL, valueCode: facts.outcome }] : []),
    ],
  };
}
